import React, { FC } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { ALL_STATUSES, BoardFilters, KPI_ITEMS, KpiItem } from "map/liveBoard";
import { BOARD_COLORS, LEG_COLOR } from "./palette";
import LegGlyph from "./LegGlyph";

const GROUPS: KpiItem["group"][] = ["Trip status", "Warnings", "Other"];

const toneColor = (item: KpiItem) =>
  item.tone === "crit" ? BOARD_COLORS.late : item.tone === "warn" ? BOARD_COLORS.warn : "inherit";

/**
 * The header chips: a count of everything worth counting, and a one-click filter behind each.
 *
 * They combine the way the side panel does -- several statuses mean any of those legs, several
 * warnings mean any of those flags, and the two groups intersect. The counts follow every filter
 * except the status chips themselves, so with a client picked they describe that client's board.
 */
const KpiBar: FC<{
  counts: Record<string, number>;
  filters: BoardFilters;
  onStatus: (status: string) => void;
  onFlag: (flag: string) => void;
}> = ({ counts, filters, onStatus, onFlag }) => {
  const narrowed = filters.statuses.size < ALL_STATUSES.length;
  const pressed = (i: KpiItem) =>
    i.status ? narrowed && filters.statuses.has(i.status) : filters.flags.has(i.flag);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
      {GROUPS.map((group) => (
        <Box key={group}>
          <Typography
            sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", mb: 0.25 }}
          >
            {group}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {KPI_ITEMS.filter((i) => i.group === group).map((i) => (
              <ButtonBase
                key={i.key}
                onClick={() => (i.status ? onStatus(i.status) : onFlag(i.flag))}
                aria-pressed={pressed(i)}
                title="Click to filter"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.75,
                  py: 0.4,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: pressed(i) ? BOARD_COLORS.accent : "divider",
                  bgcolor: pressed(i) ? "action.selected" : "background.paper",
                  fontSize: 11,
                  whiteSpace: "nowrap",
                }}
              >
                {i.status ? (
                  <LegGlyph leg={i.glyph || i.status} color={LEG_COLOR[i.glyph || i.status]} />
                ) : i.key === "backhaul" ? (
                  <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: BOARD_COLORS.backhaul }} />
                ) : null}
                <Box component="span" sx={{ fontWeight: 700, fontSize: 13, color: toneColor(i) }}>
                  {counts[i.key] ?? 0}
                </Box>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {i.label}
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default KpiBar;
